import {
    AccountTreeRounded,
    FlagRounded,
    GraphicEqRounded,
} from "@mui/icons-material";
import { Box, Stack, Typography } from "@mui/material";
import React from "react";

export type ChainNodeVariant = "parent" | "current" | "dependent";

const iconBackgrounds: Record<ChainNodeVariant, string> = {
    parent: "linear-gradient(132deg, #4B5563 3.97%, #111827 170.3%)",
    current: "linear-gradient(132deg, #3664D6 3.97%, #1C3470 170.3%)",
    dependent: "linear-gradient(132deg, #9C64D7 3.97%, #3D1865 170.3%)",
};

/**
 * Small badge used to call out the chaining additions to the builder while the
 * feature is new.
 */
export function NewBadge() {
    return (
        <Box
            component="span"
            sx={(theme) => ({
                backgroundColor: theme.palette.secondary[100],
                color: theme.palette.secondary[800],
                borderRadius: "4px",
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.5px",
                lineHeight: "16px",
                paddingInline: theme.spacing(0.75),
            })}
        >
            NEW
        </Box>
    );
}

export function ChainModelIcon(props: {
    variant: ChainNodeVariant;
    size?: number;
}) {
    const size = props.size ?? 24;
    const Icon =
        props.variant === "parent"
            ? AccountTreeRounded
            : props.variant === "current"
            ? GraphicEqRounded
            : FlagRounded;

    return (
        <Box
            sx={{
                alignItems: "center",
                background: iconBackgrounds[props.variant],
                // Larger tiles read as rounded squares rather than small chips.
                borderRadius: size > 24 ? "8px" : "4px",
                display: "flex",
                flexShrink: 0,
                height: size,
                justifyContent: "center",
                width: size,
            }}
        >
            <Icon htmlColor="white" sx={{ fontSize: size - 12 }} />
        </Box>
    );
}

/** Label above a group of controls inside the Chain source section. */
export function ChainFieldLabel(props: {
    label: string;
    isNew?: boolean;
    description?: React.ReactNode;
}) {
    return (
        <Stack direction="column" rowGap={0.25}>
            <Stack direction="row" columnGap={0.75} alignItems="center">
                <Typography
                    sx={(theme) => ({
                        color: theme.palette.gray[700],
                        fontSize: "14px",
                        fontWeight: 600,
                        lineHeight: "20px",
                    })}
                >
                    {props.label}
                </Typography>
                {props.isNew && <NewBadge />}
            </Stack>
            {props.description && (
                <Typography
                    sx={(theme) => ({
                        color: theme.palette.gray[500],
                        fontSize: "12px",
                        lineHeight: "18px",
                    })}
                >
                    {props.description}
                </Typography>
            )}
        </Stack>
    );
}

/**
 * Separates two parent gates that both have to pass. Used between peers on the
 * same level, in both the Chain source list and the Model chain map.
 */
export function AndSeparator() {
    return (
        <Stack direction="row" columnGap={1} alignItems="center" width="100%">
            {/* Solid rather than tinted: this is the operator joining two gates,
                not another piece of supporting text. */}
            <Box
                sx={(theme) => ({
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "4px",
                    color: theme.palette.white.main,
                    fontSize: "10px",
                    fontWeight: 700,
                    letterSpacing: "0.5px",
                    paddingBlock: theme.spacing(0.5),
                    paddingInline: theme.spacing(1),
                })}
            >
                AND
            </Box>
            <Box
                sx={(theme) => ({
                    borderTop: `1px solid ${theme.palette.primary[200]}`,
                    flexGrow: 1,
                })}
            />
        </Stack>
    );
}

/** Renders a variable reference such as `{{transcript}}` inline. */
export function VariableToken(props: { name: string; withBraces?: boolean }) {
    const withBraces = props.withBraces ?? true;

    return (
        <Box
            component="span"
            sx={(theme) => ({
                color: theme.palette.primary.main,
                fontFamily: "monospace",
                fontWeight: 700,
            })}
        >
            {withBraces ? `{{${props.name}}}` : props.name}
        </Box>
    );
}
